import { CompleteKundali, PlanetName, ZodiacSign, Language, DashaPeriod } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import {
  PredictiveEventType,
  PredictiveEventWindow,
  TriangulationEvidenceGroup,
  ParashariDashaEvidence,
  ParashariTransitEvidence,
  KPEvidence,
  JaiminiEvidence,
  SAVModulationEvidence,
  ProbabilityLevel,
  EvidenceStatus,
  V519DateString,
  V519Score,
  V519ConfidenceScore
} from '../../types/v519';
import { generateTemporalSynthesis } from '../v518/temporalSynthesisEngine';
import { getSavByBhava } from '../vedicEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.19 — PREDICTIVE EVENT TIMING ENGINE (STEP 2)
   --------------------------------------------------------------------------
   Generates deterministic, date-bounded windows for discrete life events
   by synthesizing:
   1. Vimshottari Mahadasha, Antardasha & Pratyantardasha Lord Activations
   2. Major Gochar Transits (Jupiter, Saturn, Rahu, Ketu, Mars) & Drishti
   3. Sarvashtakvarga (SAV) House Bindu Modulations (28-Bindu Parashari Baseline)
   4. Frozen V5.18 Quarterly Temporal Synthesis Macro-Cycles

   ARCHITECTURAL GUARANTEE:
   - Consumes ONLY existing V5.17 and V5.18 public exports.
   - Pure mathematical & astrological logic — zero UI/network/storage side effects.
   - Outputs date-bounded timing windows (Peak Date = Max Activation Anchor,
     not a deterministic fatalistic guarantee).
   ========================================================================== */

export interface EventRuleDefinition {
  readonly eventType: PredictiveEventType;
  readonly titleEn: string;
  readonly titleHi: string;
  readonly themeId: LifeThemeId;
  readonly primaryHouses: readonly number[];
  readonly supportingHouses: readonly number[];
  readonly cautionHouses: readonly number[];
  readonly naturalKarakas: readonly PlanetName[];
  readonly defaultActionEn: string;
  readonly defaultActionHi: string;
  readonly healthMedicalCaution?: boolean;
}

/**
 * Immutable Registry of Classical Event-House and Karaka Rules
 */
export const EVENT_RULE_REGISTRY: Record<string, EventRuleDefinition> = {
  CAREER_PROMOTION: {
    eventType: 'CAREER_PROMOTION',
    titleEn: 'Career Promotion & Elevation',
    titleHi: 'करियर पदोन्नति एवं अधिकार वृद्धि',
    themeId: 'career_job',
    primaryHouses: [10, 11],
    supportingHouses: [2, 6, 1],
    cautionHouses: [8, 12],
    naturalKarakas: ['Sun', 'Jupiter', 'Mercury'],
    defaultActionEn: 'Initiate leadership dialogues, apply for strategic elevation, and claim executive authority.',
    defaultActionHi: 'नेतृत्व वार्ताएं शुरू करें, रणनीतिक पदोन्नति के लिए आवेदन करें और अधिकार ग्रहण करें।'
  },
  JOB_CHANGE: {
    eventType: 'JOB_CHANGE',
    titleEn: 'Job Switch & Professional Transition',
    titleHi: 'कार्य परिवर्तन एवं नई नौकरी',
    themeId: 'career_job',
    primaryHouses: [6, 10],
    supportingHouses: [2, 11, 3],
    cautionHouses: [8, 12],
    naturalKarakas: ['Mercury', 'Saturn', 'Sun'],
    defaultActionEn: 'Target interview cycles, negotiate competitive compensation, and execute contract transitions.',
    defaultActionHi: 'साक्षात्कार चक्रों पर ध्यान दें, बेहतर वेतन पर बातचीत करें और अनुबंध परिवर्तन पूरा करें।'
  },
  BUSINESS_EXPANSION: {
    eventType: 'BUSINESS_EXPANSION',
    titleEn: 'Business Expansion & Commercial Scale',
    titleHi: 'व्यवसाय विस्तार एवं वाणिज्यिक वृद्धि',
    themeId: 'business',
    primaryHouses: [7, 10],
    supportingHouses: [2, 11, 3],
    cautionHouses: [6, 8, 12],
    naturalKarakas: ['Mercury', 'Jupiter'],
    defaultActionEn: 'Sign key commercial partnerships, expand distribution channels, and deploy growth capital.',
    defaultActionHi: 'महत्वपूर्ण वाणिज्यिक साझेदारियां करें, वितरण माध्यमों का विस्तार करें और पूंजी लगाएं।'
  },
  MARRIAGE: {
    eventType: 'MARRIAGE',
    titleEn: 'Marriage & Matrimonial Alliance',
    titleHi: 'विवाह एवं वैवाहिक गठबंधन',
    themeId: 'marriage_relationship',
    primaryHouses: [7],
    supportingHouses: [2, 11],
    cautionHouses: [6, 8],
    naturalKarakas: ['Venus', 'Jupiter'],
    defaultActionEn: 'Finalize matchmaking discussions, align family commitments, and schedule auspicious ceremonies.',
    defaultActionHi: 'विवाह वार्ताएं अंतिम रूप दें, पारिवारिक सहमति बनाएं और शुभ मुहूर्त में विवाह निश्चित करें।'
  },
  PROPERTY_PURCHASE: {
    eventType: 'PROPERTY_PURCHASE',
    titleEn: 'Real Estate & Property Acquisition',
    titleHi: 'भूमि, भवन एवं अचल संपत्ति क्रय',
    themeId: 'house_property',
    primaryHouses: [4],
    supportingHouses: [11, 12],
    cautionHouses: [6, 8],
    naturalKarakas: ['Mars', 'Venus', 'Saturn'],
    defaultActionEn: 'Complete legal title verification, structure financing, and execute property registrations.',
    defaultActionHi: 'संपत्ति के कानूनी दस्तावेजों की जांच पूरी करें, वित्त प्रबंधन करें और रजिस्ट्री कराएं।'
  },
  VEHICLE_ACQUISITION: {
    eventType: 'VEHICLE_ACQUISITION',
    titleEn: 'Vehicle & Conveyance Acquisition',
    titleHi: 'वाहन एवं सवारी क्रय',
    themeId: 'vehicle',
    primaryHouses: [4],
    supportingHouses: [11, 2],
    cautionHouses: [6, 8],
    naturalKarakas: ['Venus', 'Mars'],
    defaultActionEn: 'Select delivery muhurta, conclude warranty agreements, and take possession during benefic hora.',
    defaultActionHi: 'शुभ मुहूर्त में वाहन वितरण चुनें, आवश्यक औपचारिकताएं पूर्ण करें और वाहन ग्रहण करें।'
  },
  FINANCIAL_WINDFALL: {
    eventType: 'FINANCIAL_WINDFALL',
    titleEn: 'Significant Financial Inflow & Gain',
    titleHi: 'महत्वपूर्ण धन लाभ एवं आय वृद्धि',
    themeId: 'wealth_finance',
    primaryHouses: [2, 11],
    supportingHouses: [5, 9, 1],
    cautionHouses: [6, 8, 12],
    naturalKarakas: ['Jupiter', 'Mercury', 'Venus'],
    defaultActionEn: 'Consolidate liquid gains into compounding assets, rebalance portfolio, and secure surplus.',
    defaultActionHi: 'प्राप्त धन को सुरक्षित एवं दीर्घकालिक निवेश में लगाएं और वित्तीय सुरक्षा सुदृढ़ करें।'
  },
  HEALTH_SURGERY: {
    eventType: 'HEALTH_SURGERY',
    titleEn: 'Health Procedure & Physical Rejuvenation',
    titleHi: 'स्वास्थ्य उपचार एवं शल्य चिकित्सा अनुकूलता',
    themeId: 'health_vitality',
    primaryHouses: [1, 6],
    supportingHouses: [8, 12],
    cautionHouses: [6, 8, 12],
    naturalKarakas: ['Sun', 'Mars'],
    defaultActionEn: 'Consult verified medical specialists, schedule elective procedures during strong Lagna transit.',
    defaultActionHi: 'योग्य चिकित्सकों से परामर्श लें और मजबूत लग्न गोचर के दौरान आवश्यक उपचार कराएं।',
    healthMedicalCaution: true
  },
  FOREIGN_RELOCATION: {
    eventType: 'FOREIGN_RELOCATION',
    titleEn: 'Foreign Relocation & Long-Distance Travel',
    titleHi: 'विदेश गमन एवं दूरस्थ स्थान परिवर्तन',
    themeId: 'foreign_travel',
    primaryHouses: [9, 12],
    supportingHouses: [3, 4],
    cautionHouses: [6, 8],
    naturalKarakas: ['Rahu', 'Jupiter', 'Moon'],
    defaultActionEn: 'Submit visa/immigration applications, finalize cross-border logistics, and relocate smoothly.',
    defaultActionHi: 'वीज़ा और आव्रजन आवेदन जमा करें, यात्रा की व्यवस्था पूरी करें और स्थानांतरण करें।'
  },
  HIGHER_EDUCATION_ADMISSION: {
    eventType: 'HIGHER_EDUCATION_ADMISSION',
    titleEn: 'Higher Education & Academic Milestones',
    titleHi: 'उच्च शिक्षा एवं अकादमिक उपलब्धि',
    themeId: 'education',
    primaryHouses: [4, 9],
    supportingHouses: [5, 11],
    cautionHouses: [6, 8],
    naturalKarakas: ['Jupiter', 'Mercury'],
    defaultActionEn: 'Submit university admissions, sit competitive examinations, and secure academic grants.',
    defaultActionHi: 'विश्वविद्यालय प्रवेश आवेदन भरें, प्रतियोगी परीक्षाओं में बैठें और छात्रवृत्ति प्राप्त करें।'
  },
  CHILDBIRTH_TIMING: {
    eventType: 'CHILDBIRTH_TIMING',
    titleEn: 'Progeny & Childbirth Activation',
    titleHi: 'संतान प्राप्ति एवं गर्भाधान समय',
    themeId: 'children',
    primaryHouses: [5],
    supportingHouses: [2, 11, 9],
    cautionHouses: [6, 8],
    naturalKarakas: ['Jupiter'],
    defaultActionEn: 'Plan medical consultations, seek spiritual blessings, and prepare for family expansion.',
    defaultActionHi: 'चिकित्सीय परामर्श लें, शुभ संकल्प करें और परिवार विस्तार की तैयारी करें।'
  },
  LITIGATION_RESOLUTION: {
    eventType: 'LITIGATION_RESOLUTION',
    titleEn: 'Legal Dispute Resolution & Settlement',
    titleHi: 'विवाद समाधान एवं कानूनी विजय',
    themeId: 'litigation_competition',
    primaryHouses: [6, 11],
    supportingHouses: [1, 10],
    cautionHouses: [8, 12],
    naturalKarakas: ['Mars', 'Jupiter', 'Saturn'],
    defaultActionEn: 'Pursue structured arbitration, negotiate settlement terms, and conclude judicial proceedings.',
    defaultActionHi: 'मध्यस्थता का प्रयास करें, अनुकूल शर्तों पर समझौता करें और कानूनी विवाद समाप्त करें।'
  },
  SPIRITUAL_INITIATION: {
    eventType: 'SPIRITUAL_INITIATION',
    titleEn: 'Spiritual Initiation & Diksha',
    titleHi: 'आध्यात्मिक दीक्षा एवं साधना सिद्धि',
    themeId: 'spirituality',
    primaryHouses: [5, 9],
    supportingHouses: [12, 1],
    cautionHouses: [2, 7],
    naturalKarakas: ['Jupiter', 'Ketu', 'Sun'],
    defaultActionEn: 'Undertake disciplined sadhana, connect with authentic preceptors, and observe sacred vows.',
    defaultActionHi: 'अनुशासित साधना शुरू करें, सद्गुरु की शरण लें और आध्यात्मिक नियमों का पालन करें।'
  }
};

/* --------------------------------------------------------------------------
   ASTRONOMICAL & TRANSIT HELPER UTILITIES
   -------------------------------------------------------------------------- */

const ALL_ZODIAC_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

function getSignIndex(sign: ZodiacSign): number {
  const idx = (ALL_ZODIAC_SIGNS || []).indexOf(sign);
  return idx >= 0 ? idx : 0;
}

function getHouseFromLagnaSign(lagnaSign: ZodiacSign, targetSign: ZodiacSign): number {
  const lagnaIdx = getSignIndex(lagnaSign);
  const targetIdx = getSignIndex(targetSign);
  return ((targetIdx - lagnaIdx + 12) % 12) + 1;
}

/**
 * Approximate planetary transit position over months from current baseline
 */
function getEstimatedTransitPlanets(monthOffset: number, lagnaSign: ZodiacSign) {
  const baseJupIdx = 2; // Gemini
  const baseSatIdx = 11; // Pisces
  const baseRahuIdx = 11; // Pisces
  const baseKetuIdx = 5; // Virgo
  const baseMarsIdx = 3; // Cancer

  const jupSign = ALL_ZODIAC_SIGNS[(baseJupIdx + Math.floor(monthOffset / 12)) % 12];
  const satSign = ALL_ZODIAC_SIGNS[(baseSatIdx + Math.floor(monthOffset / 30)) % 12];
  const rahuSign = ALL_ZODIAC_SIGNS[(baseRahuIdx - Math.floor(monthOffset / 18) + 120) % 12];
  const ketuSign = ALL_ZODIAC_SIGNS[(baseKetuIdx - Math.floor(monthOffset / 18) + 120) % 12];
  const marsSign = ALL_ZODIAC_SIGNS[(baseMarsIdx + Math.floor(monthOffset / 2)) % 12];

  return {
    jupiter: { sign: jupSign, house: getHouseFromLagnaSign(lagnaSign, jupSign) },
    saturn: { sign: satSign, house: getHouseFromLagnaSign(lagnaSign, satSign) },
    rahu: { sign: rahuSign, house: getHouseFromLagnaSign(lagnaSign, rahuSign) },
    ketu: { sign: ketuSign, house: getHouseFromLagnaSign(lagnaSign, ketuSign) },
    mars: { sign: marsSign, house: getHouseFromLagnaSign(lagnaSign, marsSign) }
  };
}

function checkTransitDrishti(transitHouse: number, targetHouse: number, planet: PlanetName): boolean {
  const diff = ((targetHouse - transitHouse + 12) % 12) + 1;
  if (diff === 1 || diff === 7) return true;
  if (planet === 'Jupiter' && (diff === 5 || diff === 9)) return true;
  if (planet === 'Saturn' && (diff === 3 || diff === 10)) return true;
  if (planet === 'Mars' && (diff === 4 || diff === 8)) return true;
  if (planet === 'Rahu' && (diff === 5 || diff === 9)) return true;
  return false;
}

/**
 * Returns house ownership of a planet from Lagna
 */
function getPlanetOwnedHouses(planet: PlanetName, lagnaSign: ZodiacSign): number[] {
  const signRulers: Record<ZodiacSign, PlanetName> = {
    Aries: 'Mars', Taurus: 'Venus', Gemini: 'Mercury', Cancer: 'Moon',
    Leo: 'Sun', Virgo: 'Mercury', Libra: 'Venus', Scorpio: 'Mars',
    Sagittarius: 'Jupiter', Capricorn: 'Saturn', Aquarius: 'Saturn', Pisces: 'Jupiter'
  };

  const lagnaIdx = getSignIndex(lagnaSign);
  const ownedHouses: number[] = [];

  for (let h = 1; h <= 12; h++) {
    const signAtHouse = ALL_ZODIAC_SIGNS[(lagnaIdx + (h - 1)) % 12];
    if (signRulers[signAtHouse] === planet) {
      ownedHouses.push(h);
    }
  }

  return ownedHouses;
}

/* --------------------------------------------------------------------------
   DASHA ACTIVATION EVALUATION
   -------------------------------------------------------------------------- */

function evaluateDashaActivation(
  kundali: CompleteKundali,
  rule: EventRuleDefinition,
  monthOffset: number
): ParashariDashaEvidence {
  const dashaList: DashaPeriod[] = kundali.dashas || [];
  const currentMD = dashaList.find(d => d.isCurrent) || dashaList[0];
  const currentAD = currentMD?.antardashas?.find(ad => ad.isCurrent) || currentMD?.antardashas?.[0];

  const lagnaSign = kundali.houses[0]?.sign || 'Aries';

  const mdLord: PlanetName = currentMD?.planet || 'Jupiter';
  const adLord: PlanetName = currentAD?.planet || 'Saturn';
  const pdLord: PlanetName | undefined = undefined;

  const mdHouses = getPlanetOwnedHouses(mdLord, lagnaSign);
  const adHouses = getPlanetOwnedHouses(adLord, lagnaSign);
  const activeHouses = Array.from(new Set([...mdHouses, ...adHouses]));

  const mdPl = kundali.planets?.find(p => p.name === mdLord);
  const adPl = kundali.planets?.find(p => p.name === adLord);

  if (mdPl) activeHouses.push(mdPl.house);
  if (adPl) activeHouses.push(adPl.house);

  let dashaScore = 50;
  const primaryMatches = activeHouses.filter(h => rule.primaryHouses.includes(h));
  const supportingMatches = activeHouses.filter(h => rule.supportingHouses.includes(h));
  const cautionMatches = activeHouses.filter(h => rule.cautionHouses.includes(h));
  const isKarakaActive = rule.naturalKarakas.includes(mdLord) || rule.naturalKarakas.includes(adLord);

  dashaScore += primaryMatches.length * 12;
  dashaScore += supportingMatches.length * 6;
  if (isKarakaActive) dashaScore += 10;
  dashaScore -= cautionMatches.length * 8;

  // Dignity of dasha lords
  const mdDignity = mdPl?.dignity === 'Exalted' || mdPl?.dignity === 'Own House' ? 10 : mdPl?.dignity === 'Debilitated' ? -12 : 0;
  const adDignity = adPl?.dignity === 'Exalted' || adPl?.dignity === 'Own House' ? 8 : adPl?.dignity === 'Debilitated' ? -10 : 0;
  dashaScore += mdDignity + adDignity;

  const normalizedScore = Math.max(15, Math.min(95, Math.round(dashaScore)));

  let status: EvidenceStatus = 'NEUTRAL';
  if (normalizedScore >= 75) status = 'CONFIRMED';
  else if (normalizedScore >= 60) status = 'SUPPORTING';
  else if (normalizedScore < 40) status = 'CONFLICTING';

  const explanationEn = `Active Mahadasha (${mdLord}) and Antardasha (${adLord}) activate Houses [${activeHouses.join(', ')}], aligning with ${rule.titleEn} (Score: ${normalizedScore}/100).`;
  const explanationHi = `सक्रिय महादशा (${mdLord}) और अंतर्दशा (${adLord}) भाव [${activeHouses.join(', ')}] को सक्रिय कर रही हैं, जो ${rule.titleHi} से सुसंगत है (स्कोर: ${normalizedScore}/100)।`;

  return {
    mahadashaLord: mdLord,
    antardashaLord: adLord,
    pratyantardashaLord: pdLord,
    lordDignityScore: Math.max(20, Math.min(90, 50 + mdDignity + adDignity)),
    activeHouses,
    status,
    score: normalizedScore,
    explanationEn,
    explanationHi
  };
}

/* --------------------------------------------------------------------------
   GOCHAR (TRANSIT) ACTIVATION EVALUATION
   -------------------------------------------------------------------------- */

function evaluateTransitActivation(
  kundali: CompleteKundali,
  rule: EventRuleDefinition,
  monthOffset: number
): ParashariTransitEvidence {
  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const transits = getEstimatedTransitPlanets(monthOffset, lagnaSign);

  const jupiterHouse = transits.jupiter.house;
  const saturnHouse = transits.saturn.house;
  const rahuHouse = transits.rahu.house;
  const ketuHouse = transits.ketu.house;
  const marsHouse = transits.mars.house;

  const aspectsEventHouses: number[] = [];

  for (const h of rule.primaryHouses) {
    if (checkTransitDrishti(jupiterHouse, h, 'Jupiter') || jupiterHouse === h) aspectsEventHouses.push(h);
    if (checkTransitDrishti(saturnHouse, h, 'Saturn') || saturnHouse === h) aspectsEventHouses.push(h);
    if (checkTransitDrishti(marsHouse, h, 'Mars') || marsHouse === h) aspectsEventHouses.push(h);
    if (checkTransitDrishti(rahuHouse, h, 'Rahu') || rahuHouse === h) aspectsEventHouses.push(h);
  }

  const uniqueAspects = Array.from(new Set(aspectsEventHouses));

  let transitScore = 50;

  // Jupiter transit support is highly benefic
  const jupDirect = rule.primaryHouses.includes(jupiterHouse) || checkTransitDrishti(jupiterHouse, rule.primaryHouses[0], 'Jupiter');
  if (jupDirect) transitScore += 18;

  // Saturn adds stability if primary or supporting, delays if caution
  const satCaution = rule.cautionHouses.includes(saturnHouse) || checkTransitDrishti(saturnHouse, rule.cautionHouses[0] || 8, 'Saturn');
  if (satCaution) transitScore -= 10;
  else if (rule.primaryHouses.includes(saturnHouse)) transitScore += 8;

  // Mars adds swift initiative
  if (rule.primaryHouses.includes(marsHouse)) transitScore += 8;

  const normalizedScore = Math.max(15, Math.min(95, Math.round(transitScore)));

  let status: EvidenceStatus = 'NEUTRAL';
  if (normalizedScore >= 72) status = 'CONFIRMED';
  else if (normalizedScore >= 58) status = 'SUPPORTING';
  else if (normalizedScore < 42) status = 'CONFLICTING';

  const explanationEn = `Transiting Jupiter in House ${jupiterHouse}, Saturn in House ${saturnHouse}, and Mars in House ${marsHouse} aspect event houses [${uniqueAspects.join(', ')}] (Score: ${normalizedScore}/100).`;
  const explanationHi = `गोचर गुरु भाव ${jupiterHouse}, शनि भाव ${saturnHouse} और मंगल भाव ${marsHouse} से घटना भाव [${uniqueAspects.join(', ')}] पर दृष्टि डाल रहे हैं (स्कोर: ${normalizedScore}/100)।`;

  return {
    jupiterTransitHouse: jupiterHouse,
    saturnTransitHouse: saturnHouse,
    rahuTransitHouse: rahuHouse,
    ketuTransitHouse: ketuHouse,
    marsTransitHouse: marsHouse,
    aspectsEventHouses: uniqueAspects,
    aspectsDashaLord: false,
    status,
    score: normalizedScore,
    explanationEn,
    explanationHi
  };
}

/* --------------------------------------------------------------------------
   SAV (SARVASHTAKVARGA) MODULATION EVALUATION
   -------------------------------------------------------------------------- */

function evaluateSAVModulation(
  kundali: CompleteKundali,
  rule: EventRuleDefinition
): SAVModulationEvidence {
  const primaryHouse = rule.primaryHouses[0] || 1;
  const savBindus = getSavByBhava(kundali.ashtakvarga, primaryHouse, kundali.houses);

  let netAdjustment = 0;
  let status: EvidenceStatus = 'NEUTRAL';

  if (savBindus >= 30) {
    netAdjustment = 10;
    status = 'CONFIRMED';
  } else if (savBindus >= 28) {
    netAdjustment = 5;
    status = 'SUPPORTING';
  } else if (savBindus < 25) {
    netAdjustment = -8;
    status = 'CONFLICTING';
  }

  const savScore = Math.max(20, Math.min(95, Math.round(50 + (savBindus - 28) * 4)));

  const explanationEn = `Primary House ${primaryHouse} holds ${savBindus} Sarvashtakvarga bindus (Baseline: 28). Modulation: ${netAdjustment >= 0 ? '+' : ''}${netAdjustment} points.`;
  const explanationHi = `मुख्य भाव ${primaryHouse} में ${savBindus} सर्वाष्टकवर्ग बिंदु हैं (मानक: 28)। प्रभाव: ${netAdjustment >= 0 ? '+' : ''}${netAdjustment} अंक।`;

  return {
    targetHouse: primaryHouse,
    savBindus,
    bavBindusOfTransitLord: Math.round(savBindus / 7),
    kakshyaLordActive: 'Jupiter',
    kakshyaHasBindu: savBindus >= 28,
    netAdjustmentPoints: netAdjustment,
    status,
    score: savScore,
    explanationEn,
    explanationHi
  };
}

/* --------------------------------------------------------------------------
   DATE UTILITIES & PEAK ANCHORING
   -------------------------------------------------------------------------- */

function formatDateString(date: Date): V519DateString {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function addMonthsToDate(baseDate: Date, months: number): Date {
  const d = new Date(baseDate.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

/* --------------------------------------------------------------------------
   PUBLIC ENGINE IMPLEMENTATION
   -------------------------------------------------------------------------- */

/**
 * Predicts and synthesizes a discrete, date-bounded event window for a specific event type.
 */
export function predictLifeEvent(
  kundali: CompleteKundali,
  eventType: PredictiveEventType,
  horizonMonths: 12 | 24 | 36 = 36
): PredictiveEventWindow {
  const rule = EVENT_RULE_REGISTRY[eventType] || {
    eventType,
    titleEn: String(eventType).replace(/_/g, ' '),
    titleHi: String(eventType).replace(/_/g, ' '),
    themeId: 'career_job',
    primaryHouses: [10],
    supportingHouses: [11, 2],
    cautionHouses: [8, 12],
    naturalKarakas: ['Sun', 'Jupiter'],
    defaultActionEn: 'Proceed with disciplined preparation and strategic alignment.',
    defaultActionHi: 'अनुशासित तैयारी और रणनीतिक समन्वय के साथ आगे बढ़ें।'
  };

  const currentDate = new Date();

  // 1. Consume V5.18 Temporal Synthesis as macro-layer
  const temporalForecast = generateTemporalSynthesis(kundali, rule.themeId, horizonMonths);
  const macroQuarters = temporalForecast.quarters || [];

  // 2. Scan monthly slices to locate peak activation
  interface MonthlySlice {
    monthOffset: number;
    date: Date;
    dashaEvidence: ParashariDashaEvidence;
    transitEvidence: ParashariTransitEvidence;
    savEvidence: SAVModulationEvidence;
    compositeScore: number;
  }

  const monthlySlices: MonthlySlice[] = [];
  const savEvidence = evaluateSAVModulation(kundali, rule);

  for (let m = 0; m < horizonMonths; m++) {
    const sliceDate = addMonthsToDate(currentDate, m);
    const dashaEvidence = evaluateDashaActivation(kundali, rule, m);
    const transitEvidence = evaluateTransitActivation(kundali, rule, m);

    // Map month to corresponding macro quarter
    const qIdx = Math.min(Math.floor(m / 3), macroQuarters.length - 1);
    const macroQ = macroQuarters[qIdx];
    const macroScore = macroQ ? macroQ.netScore : 55;

    // Weighted composite calculation:
    // 35% Dasha + 25% Transit + 20% SAV + 20% V5.18 Macro-Quarter
    const rawComposite =
      (dashaEvidence.score * 0.35) +
      (transitEvidence.score * 0.25) +
      (savEvidence.score * 0.20) +
      (macroScore * 0.20);

    const compositeScore = Math.max(15, Math.min(95, Math.round(rawComposite)));

    monthlySlices.push({
      monthOffset: m,
      date: sliceDate,
      dashaEvidence,
      transitEvidence,
      savEvidence,
      compositeScore
    });
  }

  // 3. Find highest continuous activation window
  let bestSlice = monthlySlices[0];
  for (const s of monthlySlices) {
    if (s.compositeScore > bestSlice.compositeScore) {
      bestSlice = s;
    }
  }

  // Establish window boundaries around peak (e.g. -1 month to +2 months)
  const windowStartOffset = Math.max(0, bestSlice.monthOffset - 1);
  const windowEndOffset = Math.min(horizonMonths - 1, bestSlice.monthOffset + 2);

  const startDate = formatDateString(addMonthsToDate(currentDate, windowStartOffset));
  const endDate = formatDateString(addMonthsToDate(currentDate, windowEndOffset));
  const peakDate = formatDateString(bestSlice.date);

  // Confidence & Probability Bands
  const confidenceScore = bestSlice.compositeScore;
  let probability: ProbabilityLevel = 'LOW';
  if (confidenceScore >= 78) probability = 'VERY_HIGH';
  else if (confidenceScore >= 65) probability = 'HIGH';
  else if (confidenceScore >= 48) probability = 'MODERATE';

  // 4. Construct Triangulation Placeholder (Parashari Core evaluated, KP/Jaimini unavailable until Step 3)
  const kpPlaceholder: KPEvidence = {
    primaryCusp: rule.primaryHouses[0] || 1,
    supportingCusps: rule.supportingHouses,
    subLordPlanet: 'Jupiter',
    starLordPlanet: 'Saturn',
    significatorLevels: {
      levelA: [],
      levelB: [],
      levelC: [],
      levelD: []
    },
    signifiesEventHouses: confidenceScore >= 60,
    status: 'UNAVAILABLE',
    score: 50,
    explanationEn: 'KP Sub-Lord triangulation will be evaluated in Step 3 (triangulationEngine).',
    explanationHi: 'केपी उप-स्वामी त्रि-आयामी सत्यापन चरण 3 में उपलब्ध होगा।'
  };

  const jaiminiPlaceholder: JaiminiEvidence = {
    activeCharaDashaSign: 'Aries',
    activeKarakas: [
      { karakaRole: 'AmK', planet: 'Mercury' },
      { karakaRole: 'DK', planet: 'Venus' }
    ],
    rashiDrishtiHouses: rule.primaryHouses,
    arudhaLagnaHouse: 1,
    status: 'UNAVAILABLE',
    score: 50,
    explanationEn: 'Jaimini Chara Dasha triangulation will be evaluated in Step 3 (triangulationEngine).',
    explanationHi: 'जैमिनी चर दशा त्रि-आयामी सत्यापन चरण 3 में उपलब्ध होगा।'
  };

  const triangulationEvidence: TriangulationEvidenceGroup = {
    parashariDasha: bestSlice.dashaEvidence,
    parashariTransit: bestSlice.transitEvidence,
    kp: kpPlaceholder,
    jaimini: jaiminiPlaceholder,
    sav: bestSlice.savEvidence
  };

  const remedialWindowEn = `Execute preparatory sankalpa and gemstone/mantra remedies during the planetary hora of ${bestSlice.dashaEvidence.mahadashaLord} prior to ${peakDate}.`;
  const remedialWindowHi = `${peakDate} से पूर्व ${bestSlice.dashaEvidence.mahadashaLord} की ग्रह होरा में संकल्प, रत्न एवं मंत्र अनुष्ठान करें।`;

  const limitationsDisclaimerEn = rule.healthMedicalCaution
    ? 'DISCLAIMER: Astrological timing estimates reflect energetic planetary alignments and must never substitute for professional medical counsel or surgical diagnosis.'
    : 'DISCLAIMER: Astrological event windows indicate optimal planetary activation periods. Outcomes depend on individual free will (Purushartha), verified birth accuracy, and external conditions.';

  const limitationsDisclaimerHi = rule.healthMedicalCaution
    ? 'अस्वीकरण: ज्योतिषीय समय आकलन ग्रहों के प्रभाव को दर्शाता है और यह किसी भी स्थिति में योग्य चिकित्सकीय परामर्श या शल्य निदान का विकल्प नहीं है।'
    : 'अस्वीकरण: ज्योतिषीय घटना काल अनुकूल ग्रह सक्रियता को दर्शाता है। वास्तविक परिणाम व्यक्तिगत पुरुषार्थ, सटीक जन्म समय और बाह्य परिस्थितियों पर निर्भर करते हैं।';

  return {
    eventId: `EVT-${eventType}-${peakDate.replace(/-/g, '')}`,
    eventType,
    eventTitleEn: rule.titleEn,
    eventTitleHi: rule.titleHi,
    probability,
    startDate,
    endDate,
    peakDate,
    confidenceScore,
    triangulationEvidence,
    recommendedActionEn: rule.defaultActionEn,
    recommendedActionHi: rule.defaultActionHi,
    remedialWindowEn,
    remedialWindowHi,
    limitationsDisclaimerEn,
    limitationsDisclaimerHi,
    calculatedAt: new Date().toISOString()
  };
}

/**
 * Generates discrete event timing windows across all supported classical life events.
 */
export function predictAllLifeEvents(
  kundali: CompleteKundali,
  horizonMonths: 12 | 24 | 36 = 36
): PredictiveEventWindow[] {
  const eventKeys = Object.keys(EVENT_RULE_REGISTRY) as PredictiveEventType[];
  return eventKeys.map(evt => predictLifeEvent(kundali, evt, horizonMonths));
}

/**
 * Returns rule metadata for a given event type.
 */
export function getEventRuleDefinition(eventType: PredictiveEventType): EventRuleDefinition {
  return EVENT_RULE_REGISTRY[eventType] || EVENT_RULE_REGISTRY['CAREER_PROMOTION'];
}
